/**
 * Zoom and pan of an image fitted in a box. The image is drawn at its fitted size, centred, then moved by `x`/`y` and
 * scaled by `s` around its centre (CSS `translate(x, y) scale(s)`, origin at the centre).
 */
export interface View {
  s: number;
  x: number;
  y: number;
}
export const MIN_SCALE = 1;
export const MAX_SCALE = 8;
export const IDENTITY: View = { s: 1, x: 0, y: 0 };

/** Keeps the image covering the box where it is larger than the box, and centred where it is smaller. */
export function clamp(v: View, fitted: { w: number; h: number }, box: { w: number; h: number }): View {
  const s = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.s));
  const limX = Math.max(0, (fitted.w * s - box.w) / 2);
  const limY = Math.max(0, (fitted.h * s - box.h) / 2);
  return { s, x: Math.min(limX, Math.max(-limX, v.x)), y: Math.min(limY, Math.max(-limY, v.y)) };
}

/**
 * Scales to `next` keeping the image point under `p` (measured from the box centre) where it is: the point at
 * p = t + s·v stays put, so t' = p − (s'/s)(p − t).
 */
export function zoomAt(v: View, next: number, p: { x: number; y: number }): View {
  const s = Math.min(MAX_SCALE, Math.max(MIN_SCALE, next));
  const k = s / v.s;
  return { s, x: p.x - k * (p.x - v.x), y: p.y - k * (p.y - v.y) };
}
